import { memo, useContext, useRef } from 'react';
import cn from 'classnames';
import type { NodeToRenderProps, CustomGenericNodeData, CustomNodeData } from '../types/custom-nodes.type.ts';
import type { GenericInfoContentItem, SelectboxOptionItem } from '@src/scenario-editor/render-utils/types.ts';
import {applyGenericHocs} from '@src/scenario-editor/utils';
import { CustomTooltip } from '../../tooltip/Tooltip.tsx';
import { IconFromSVG } from '@src/components/icon/utils.tsx';
import { nodesConfigAtom, reactFlowInstanceAtom } from '@src/atoms';
import { useAtom } from 'jotai';
import { ReactflowInstanceContext } from '@src/scenario-editor/ScenarioEditor.tsx';
import { useNodeDnD } from '@src/components/custom-nodes/generic-node/hooks/useDnD.hook.ts';

export const MenuItem = ({ children, className, ...props }: NodeToRenderProps) => {
  return (
  <div
    className={
      cn([
        "flex rounded-md p-2 pr-3 border-2 border-transparent bg-white h-full justify-center items-center w-fit text-sm truncate",
        className,
      ])
    }
    {...props}
    style={props.style}
  >{children}</div>
)};

const getLabelByPath = (data: CustomGenericNodeData, path: string) => {
  const pathArr = path.split('.');
  let skipIteration = false;
  let current = data;

  if (pathArr.length === 1) {
    if (path in data) return data[path as keyof CustomGenericNodeData];

    return undefined;
  }

  pathArr.forEach((key) => {
    if (!(Object.keys(current).includes(key))) {
      skipIteration = true;
      return;
    }

    current = current[key as keyof CustomGenericNodeData] as never;
  });

  if (skipIteration) {
    return undefined;
  }

  return typeof current === 'object' ? JSON.stringify(current) : current;
}

export const NodeItem = ({ data, ...props }: NodeToRenderProps) => {
  const { bgColor, title, titleDescription, rootLabelField, nodeType } = data as CustomGenericNodeData;
  const [nodesConfig] = useAtom(nodesConfigAtom);
  const reactFlowInstance = useContext(ReactflowInstanceContext);
  const instance = reactFlowInstance.get(reactFlowInstanceAtom)!;
  const nodeRef = useRef<HTMLDivElement>(null);

  const { isNodeDraggable } = useNodeDnD({
    instance,
    nodeRef,
    id: props.id,
    data: data as CustomNodeData,
  })

  const nodeConfigItem = nodesConfig.map((section) => section.items.find((item) => item.nodeType === nodeType)).filter(Boolean)[0];
  const relatedOptionLabels = (data! as CustomGenericNodeData).nodeInfoContent.find((item: GenericInfoContentItem) => item.fieldName === rootLabelField)?.options;
  let currentLabel = (getLabelByPath(data as CustomGenericNodeData, rootLabelField!) as string) ?? title;
  const backgroundColor = nodeConfigItem?.bgColor ?? bgColor;
  if (relatedOptionLabels) {
    currentLabel = (relatedOptionLabels ?? []).find((item: SelectboxOptionItem) => item.value === currentLabel)?.label ?? currentLabel;
  }
  const cardType = (data as CustomGenericNodeData).cardType;

  return (
    <div
      ref={nodeRef}
      data-testid={`node=>${(data as CustomGenericNodeData).nodeType}`}
      className={
        cn([
          props.className,
          "node-item",
          "flex flex-col py-2 px-2 rounded-md border-2 border-transparent justify-start items-center w-fit text-sm h-[94px]",
          {
            'bg-white': cardType === 'default' || !cardType,
            'bg-transparent': cardType === 'ghost',
          }
        ])
      }
      style={props.style}
      draggable={isNodeDraggable}
    >
      {nodeConfigItem?.icon && (
        <div style={{ backgroundColor: backgroundColor ?? 'var(--input)' }} className="rounded-md w-8 h-8 flex justify-center items-center">
          <IconFromSVG svg={nodeConfigItem?.icon ?? ''} />
        </div>
      )}
      <div className="grow flex justify-center items-center mt-2">
        {currentLabel ? (
          <CustomTooltip content={currentLabel}>
            <p className="text-center text-xs font-[500] truncate max-w-full line-clamp-3 break-words whitespace-break-spaces leading-4">{currentLabel}</p>
          </CustomTooltip>
        ) : (
          <p className="text-center text-xs text-label-foreground font-[500] truncate max-w-full break-all line-clamp-3 whitespace-break-spaces leading-4">{titleDescription}</p>
        )}
      </div>

    </div>
  )
};

const WrappedNodeToRender = applyGenericHocs(NodeItem);

const GenericItem: React.FC<NodeToRenderProps> = (props: NodeToRenderProps) => {
  const { title } = props.data as CustomGenericNodeData;
  return (
    <>
      <WrappedNodeToRender {...props}>{title}</WrappedNodeToRender>
    </>
  );
};

export const GenericNode = memo(GenericItem);
